import { useForm, zodResolver } from "@mantine/form";

import {
  LABOR_QUESTIONNAIRE_DISPLAY_NAMES,
  QuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/labor-questionnaires";
import { LaborStageSelectField } from "@/encounters/components/EncounterLabor/EncounterLaborAmend/LaborStageSelectField";
import { CervicalExamFormFields } from "@/encounters/components/EncounterLabor/flowsheet-forms/cervical-exam/components/CervicalExamFormFields";
import {
  CERVICAL_EXAM_DATE_LINK_ID,
  CERVICAL_EXAM_DATE_TIME_LINK_ID,
  CERVICAL_EXAM_DILATION_LINK_ID,
  CERVICAL_EXAM_EFFACEMENT_LINK_ID,
  CERVICAL_EXAM_QUESTIONNAIRE_NAME,
  CERVICAL_EXAM_ROOT_LINK_ID,
  CERVICAL_EXAM_STATION_LINK_ID,
  CERVICAL_EXAM_TIME_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/cervical-exam/constants/cervical-exam-link-ids";
import type { CervicalExamFlowsheetDrawerProps } from "@/encounters/components/EncounterLabor/flowsheet-forms/cervical-exam/types/cervical-exam-drawer";
import type { CervicalExamFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/cervical-exam/types/cervical-exam-form";
import {
  getDefaultCervicalExamFlowsheetFormValues,
  normalizeCervicalExamFlowsheetFormValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/cervical-exam/utils/cervical-exam-form-defaults";
import {
  mapCervicalExamFormValuesToQuestionnaireValues,
  mapQuestionnaireResponseToCervicalExamFormValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/cervical-exam/utils/cervical-exam-questionnaire-mapping";
import { cervicalExamFlowsheetFormSchema } from "@/encounters/components/EncounterLabor/flowsheet-forms/cervical-exam/zod-schemas/cervical-exam-flowsheet-form";
import { LaborFlowsheetDrawerShell } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetDrawerShell";
import {
  type LaborFlowsheetDrawerEditContext,
  useLaborFlowsheetDrawerPersist,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/useLaborFlowsheetDrawerPersist";

const CERVICAL_EXAM_DISPLAY_NAME =
  LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.CervicalExam];

/** Builds edit-mode persist context from drawer props; undefined for quick-add. */
function getCervicalExamEditContext(
  props: CervicalExamFlowsheetDrawerProps,
): LaborFlowsheetDrawerEditContext | undefined {
  if (props.mode !== "edit") {
    return undefined;
  }

  return {
    observation: props.observation,
    questionnaireDisplayName: props.questionnaireDisplayName,
  };
}

export function mergeCervicalExamHydratedEditFormValues({
  mapped,
  hydratedQuestionnaireValues,
}: {
  mapped: CervicalExamFlowsheetFormValues;
  hydratedQuestionnaireValues: Record<string, unknown>;
}): CervicalExamFlowsheetFormValues {
  const hydratedRoot = hydratedQuestionnaireValues[CERVICAL_EXAM_ROOT_LINK_ID] as
    | Record<string, unknown>
    | undefined;
  const hydratedDateTime = hydratedRoot?.[CERVICAL_EXAM_DATE_TIME_LINK_ID] as
    | Record<string, unknown>
    | undefined;
  const hydratedExamDate = hydratedDateTime?.[CERVICAL_EXAM_DATE_LINK_ID];
  const hydratedExamTime = hydratedDateTime?.[CERVICAL_EXAM_TIME_LINK_ID];
  const hydratedDilation = hydratedRoot?.[CERVICAL_EXAM_DILATION_LINK_ID];
  const hydratedEffacement = hydratedRoot?.[CERVICAL_EXAM_EFFACEMENT_LINK_ID];
  const hydratedStation = hydratedRoot?.[CERVICAL_EXAM_STATION_LINK_ID];

  return {
    ...mapped,
    examDate: hydratedExamDate instanceof Date ? hydratedExamDate : mapped.examDate,
    examTime: typeof hydratedExamTime === "string" ? hydratedExamTime : mapped.examTime,
    dilation:
      typeof hydratedDilation === "string" || typeof hydratedDilation === "number"
        ? hydratedDilation
        : mapped.dilation,
    effacement:
      typeof hydratedEffacement === "string" || typeof hydratedEffacement === "number"
        ? hydratedEffacement
        : mapped.effacement,
    station:
      typeof hydratedStation === "string" || typeof hydratedStation === "number"
        ? hydratedStation
        : mapped.station,
  };
}

export function CervicalExamFlowsheetDrawer(props: CervicalExamFlowsheetDrawerProps) {
  const { opened, onClose, clientId, encounterId, mode } = props;

  const form = useForm<CervicalExamFlowsheetFormValues>({
    initialValues: getDefaultCervicalExamFlowsheetFormValues(),
    validate: zodResolver(cervicalExamFlowsheetFormSchema),
  });

  const {
    isLoading,
    isSubmitting,
    loadError,
    dateTimeUtcDateAnchors,
    drawerLaborStageLoincCode,
    setDrawerLaborStageLoincCode,
    showLaborStageInDrawer,
    drawerTitle,
    handleSubmit,
  } = useLaborFlowsheetDrawerPersist<CervicalExamFlowsheetFormValues>({
    opened,
    mode,
    onClose,
    clientId,
    encounterId,
    questionnaireName: CERVICAL_EXAM_QUESTIONNAIRE_NAME,
    displayName: CERVICAL_EXAM_DISPLAY_NAME,
    timerKey: "cervical",
    form,
    parseAndNormalizeFormValues: (values) =>
      normalizeCervicalExamFlowsheetFormValues(cervicalExamFlowsheetFormSchema.parse(values)),
    getDefaultFormValues: getDefaultCervicalExamFlowsheetFormValues,
    mapFormValuesToQuestionnaireValues: mapCervicalExamFormValuesToQuestionnaireValues,
    mapQuestionnaireResponseToFormValues: mapQuestionnaireResponseToCervicalExamFormValues,
    mergeHydratedEditFormValues: mergeCervicalExamHydratedEditFormValues,
    questionnaireNotFoundMessage: "Cervical exam questionnaire not found.",
    editEntryNotFoundMessage: "Could not load existing cervical exam entry.",
    editContext: getCervicalExamEditContext(props),
    onRefresh: mode === "quick-add" ? props.onRefresh : undefined,
    title: props.title,
  });

  return (
    <LaborFlowsheetDrawerShell
      opened={opened}
      onClose={onClose}
      error={loadError}
      title={drawerTitle}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      dateTimeUtcDateAnchors={dateTimeUtcDateAnchors}
      formId={`labor-flowsheet-form-${QuestionnaireType.CervicalExam}`}
      submitButtonText={mode === "edit" ? "Update" : "Add To Flowsheet"}
      headerSection={
        showLaborStageInDrawer && drawerLaborStageLoincCode ? (
          <LaborStageSelectField
            value={drawerLaborStageLoincCode}
            onChange={setDrawerLaborStageLoincCode}
          />
        ) : null
      }
    >
      <CervicalExamFormFields form={form} mode={mode} clientId={clientId} isDrawerOpened={opened} />
    </LaborFlowsheetDrawerShell>
  );
}
